/* eslint-disable no-unused-vars */
import React from 'react';
import { useSelector } from 'react-redux';
import d3 from '../../utils/d3Importer';
import { useD3 } from '../../hooks/useD3';

export default function AgeContent() {
  const data = useSelector((state) => state.data.data);
  const id = useSelector((state) => state.data.id);
  let stageWidth = useSelector((state) => state.browserUtils.width);
  stageWidth = stageWidth > 630 ? 630 : stageWidth;

  const item = data.find((d) => d.id === id);

  const cap = 8;

  const margin = { top: 20, right: 20, bottom: 20, left: 60 };
  const height = 200;

  const refBar = useD3((svg) => {
    svg.select('.bar-chart').html('');
    svg.select('.x-axis').html('');
    svg.select('.y-axis').html('');

    svg.style('color', '#555');

    const chartWidth = stageWidth - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const barData = [
      {
        metric: 'Male',
        value: Number(item?.maleAge) || 0,
        se: Number(item?.seAgeMales) || 0,
      },
      {
        metric: 'Female',
        value: Number(item?.femaleAge) || 0,
        se: Number(item?.seAgeFemales) || 0,
      },
      {
        metric: 'Person',
        value: Number(item?.personAge) || 0,
        se: Number(item?.seAgePersons) || 0,
      },
    ];

    const maxExtent =
      d3.max(barData.filter((d) => d.value > 0).map((d) => d.value + d.se)) ??
      0;

    const y = d3
      .scaleBand()
      .domain(['Person', 'Male', 'Female'])
      .range([0, chartHeight])
      .paddingInner(0.2);

    const x = d3
      .scaleLinear()
      .domain([0, Math.round(maxExtent * 1.1)])
      .range([0, chartWidth])
      .nice();

    const yAxis = (g) => {
      g.call(d3.axisLeft(y));
    };

    svg
      .select('.chart')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    svg.select('.y-axis').call(yAxis);

    if (item?.id > 0) {
      const xAxis = (g) => {
        g.attr('transform', `translate(0,${chartHeight})`);
        g.call(d3.axisBottom(x).ticks(5).tickSize(-chartHeight)).call(() => {
          g.selectAll('line').attr('stroke-dasharray', '2,4');
          g.selectAll('line').style('opacity', (d, i) => (i === 0 ? 0 : 0.5));
        });
      };
      svg.select('.x-axis').call(xAxis);

      const row = svg
        .select('.bar-chart')
        .selectAll('g.row')
        .data(barData, (d) => d.metric)
        .join('g')
        .attr('class', 'row')
        .attr('transform', (d) => `translate(0, ${y(d.metric)})`);

      // ----- Bars (only when value > 0) -----
      row
        .selectAll('rect.bar')
        .data((d, i) => (d.value > 0 ? [{ ...d, i }] : []))
        .join('rect')
        .attr('class', (d) => `bar bar${d.i}`)
        .attr('x', 0)
        .attr('y', 0)
        .attr('height', y.bandwidth())
        .attr('width', (d) => x(d.value))
        .style('stroke', '#bbb')
        .style('fill', '#4d9e42')
        .style('stroke-width', 1.2);

      // ----- Value label (age or fallback text) -----
      row
        .selectAll('text.value-label')
        .data((d) => [d])
        .join('text')
        .attr('class', 'value-label')
        .attr('x', 6)
        .attr('y', y.bandwidth() / 2)
        .attr('dy', '0.35em')
        .text((d) => (d.value > 0 ? `${d.value} years` : 'No available data'))
        .style('font-weight', 800)
        .style('font-size', '15px')
        .style('fill', (d) => (d.value > 0 ? '#fff' : '#555'))
        .style('pointer-events', 'none');

      // ----- Error bars (± SE, only when value > 0) -----
      const err = row
        .selectAll('g.error-bar')
        .data((d) => (d.value > 0 ? [d] : []))
        .join('g')
        .attr('class', 'error-bar');

      err
        .selectAll('line.range')
        .data((d) => [d])
        .join('line')
        .attr('x1', (d) => x(Math.max(0, d.value - d.se)))
        .attr('x2', (d) => x(d.value + d.se))
        .attr('y1', y.bandwidth() / 2)
        .attr('y2', y.bandwidth() / 2)
        .style('stroke', '#555')
        .style('stroke-width', 1.5);

      err
        .selectAll('line.cap-left')
        .data((d) => [d])
        .join('line')
        .attr('x1', (d) => x(Math.max(0, d.value - d.se)))
        .attr('x2', (d) => x(Math.max(0, d.value - d.se)))
        .attr('y1', y.bandwidth() / 2 - cap / 2)
        .attr('y2', y.bandwidth() / 2 + cap / 2)
        .style('stroke', '#555')
        .style('stroke-width', 1.5);

      err
        .selectAll('line.cap-right')
        .data((d) => [d])
        .join('line')
        .attr('x1', (d) => x(d.value + d.se))
        .attr('x2', (d) => x(d.value + d.se))
        .attr('y1', y.bandwidth() / 2 - cap / 2)
        .attr('y2', y.bandwidth() / 2 + cap / 2)
        .style('stroke', '#555')
        .style('stroke-width', 1.5);
    }
  });

  const style = {
    width: stageWidth,
    height,
    marginRight: '0px',
    marginLeft: '0px',
  };

  /* const wrapStyle = {
    display: item?.id > 0 ? 'block' : 'none',
  }; */

  return (
    <section className="chart-wrap">
      <h4>Average age in years for occupation</h4>
      <svg ref={refBar} style={style}>
        <g className="chart">
          <g className="bar-chart" />
          <g className="x-axis" />
          <g className="y-axis" />
        </g>
      </svg>
    </section>
  );
}
